/**
 * The right-hand rail.
 *
 * Present on every public page except the homepage, which has its own job —
 * get the visitor into a search — and does not want a column of anything else
 * competing with the box.
 *
 * The rule for what earns a place here: **a rail widget must be something the
 * index already knows.** It would be easy to fill this column with promises
 * about modules that do not exist yet, and a reader who clicks one and finds
 * nothing stops trusting the parts that do work. So every card below renders
 * real, sourced data or it does not render at all — and the one card that does
 * mention unbuilt work says plainly that it is unbuilt.
 *
 * The cards are also the first, smallest form of the modules this product is
 * growing into: the market snapshot is NaijaIndex Market, the benchmarks are
 * NaijaIndex Prices, the coverage figures are NaijaIndex Analytics. Building
 * them as rail widgets first means each one earns its page later on evidence
 * that people use it, rather than on a roadmap.
 */

import { useQuery } from '@tanstack/react-query'
import { Link, useLocation } from 'react-router-dom'
import { ChangePill, Sparkline, directionOf } from '@/components/data/marks'
import { ArrowRightIcon, ChartIcon, SourceIcon } from '@/components/ui/icons'
import { Panel, Skeleton } from '@/components/ui/primitives'
import { marketApi, searchApi } from '@/services/endpoints'
import { formatNumber, formatRelative } from '@/utils/format'

export function SiteRail() {
  const { pathname } = useLocation()

  const rates = useQuery({ queryKey: ['market-rates'], queryFn: marketApi.rates })
  const stats = useQuery({ queryKey: ['stats'], queryFn: searchApi.stats })

  // The markets page already leads with all of this, in more detail. Repeating
  // it three inches to the right would be noise, so the rail drops the market
  // cards there and keeps the ones that still add something.
  const onMarkets = pathname.startsWith('/markets')

  return (
    <div className="sticky top-20 space-y-4">
      {!onMarkets ? <MarketSnapshot rates={rates.data} loading={rates.isLoading} /> : null}
      {!onMarkets ? <Benchmarks rates={rates.data} /> : null}
      <Coverage stats={stats.data} loading={stats.isLoading} />
      <Provenance />
      <MoreFrom />
    </div>
  )
}

/* -- NaijaIndex Market ----------------------------------------------------- */
function MarketSnapshot({
  rates, loading,
}: {
  rates: ReturnType<typeof useQuery<Awaited<ReturnType<typeof marketApi.rates>>>>['data']
  loading: boolean
}) {
  if (loading) {
    return (
      <Panel>
        <RailHeading>Markets</RailHeading>
        <Skeleton className="mt-3 h-16" />
      </Panel>
    )
  }

  const usd = rates?.fx.find((rate) => rate.symbol.startsWith('USD/'))
  const movers = [...(rates?.indices ?? [])]
    .filter((index) => index.change_percent !== null)
    .sort(
      (a, b) =>
        Math.abs(Number(b.change_percent)) - Math.abs(Number(a.change_percent)),
    )
    .slice(0, 3)

  if (!usd && movers.length === 0) return null

  return (
    <Panel>
      <RailHeading href="/markets">Markets</RailHeading>

      {usd ? (
        <div className="mt-3 flex items-end justify-between gap-2">
          <div>
            <p className="text-lg font-semibold text-[var(--text-primary)]">
              ₦
              {Number(usd.amount).toLocaleString(undefined, {
                minimumFractionDigits: 2, maximumFractionDigits: 2,
              })}
            </p>
            <p className="text-2xs text-[var(--text-tertiary)]">
              per 1 USD · {formatRelative(usd.observed_at)}
            </p>
          </div>
          <Sparkline
            points={usd.history}
            direction={directionOf(usd.change_percent)}
            width={56}
            height={22}
          />
        </div>
      ) : null}

      {movers.length > 0 ? (
        <ul className="mt-3 space-y-1.5 border-t border-[var(--border-subtle)] pt-3">
          {movers.map((index) => (
            <li key={index.symbol} className="flex items-center justify-between gap-2">
              <span className="truncate text-xs text-[var(--text-secondary)]">
                {index.name}
              </span>
              <ChangePill changePercent={index.change_percent} />
            </li>
          ))}
        </ul>
      ) : null}
    </Panel>
  )
}

/* -- NaijaIndex Prices ----------------------------------------------------- */
function Benchmarks({
  rates,
}: {
  rates: ReturnType<typeof useQuery<Awaited<ReturnType<typeof marketApi.rates>>>>['data']
}) {
  const benchmarks = (rates?.index_benchmarks ?? []).slice(0, 3)
  if (benchmarks.length === 0) return null

  return (
    <Panel>
      <RailHeading href="/products">Price benchmarks</RailHeading>
      <ul className="mt-3 space-y-2.5">
        {benchmarks.map((benchmark) => (
          <li key={benchmark.symbol}>
            <div className="flex items-baseline justify-between gap-2">
              <span className="truncate text-xs text-[var(--text-secondary)]">
                {benchmark.name}
              </span>
              <span className="shrink-0 text-sm font-medium tabular-nums text-[var(--text-primary)]">
                ₦{Number(benchmark.amount).toLocaleString(undefined, {
                  maximumFractionDigits: 0,
                })}
              </span>
            </div>
            <p className="text-2xs text-[var(--text-tertiary)]">
              median {benchmark.unit} · {formatNumber(benchmark.observation_count)} offers
            </p>
          </li>
        ))}
      </ul>
      {/* The sample size is on every row for the same reason it is on the
          market page: a median over four offers and one over forty are
          different claims. */}
    </Panel>
  )
}

/* -- NaijaIndex Analytics -------------------------------------------------- */
function Coverage({
  stats, loading,
}: {
  stats: ReturnType<typeof useQuery<Awaited<ReturnType<typeof searchApi.stats>>>>['data']
  loading: boolean
}) {
  if (loading) {
    return (
      <Panel>
        <RailHeading>Coverage</RailHeading>
        <Skeleton className="mt-3 h-16" />
      </Panel>
    )
  }
  if (!stats) return null

  const rows: Array<[string, string]> = [
    ['Businesses', formatNumber(stats.businesses)],
    ['Verified', formatNumber(stats.verified_businesses)],
    ['Products', formatNumber(stats.products)],
    ['Current prices', formatNumber(stats.prices)],
    ['States', `${stats.states_covered} of 37`],
  ]

  return (
    <Panel>
      <RailHeading href="/states">Coverage</RailHeading>
      <dl className="mt-3 space-y-1.5">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-baseline justify-between gap-2">
            <dt className="text-xs text-[var(--text-secondary)]">{label}</dt>
            <dd className="text-xs font-medium tabular-nums text-[var(--text-primary)]">
              {value}
            </dd>
          </div>
        ))}
      </dl>
      <p className="mt-2.5 text-2xs leading-relaxed text-[var(--text-tertiary)]">
        Real counts. If the index holds nine businesses it says nine — rounding
        up on a page like this is the first small lie a data product tells.
      </p>
    </Panel>
  )
}

/* -- Provenance ------------------------------------------------------------ */
function Provenance() {
  return (
    <Panel>
      <RailHeading>How to read this</RailHeading>
      <ul className="mt-3 space-y-2 text-2xs leading-relaxed text-[var(--text-secondary)]">
        <li className="flex gap-2">
          <SourceIcon size={12} className="mt-0.5 shrink-0 text-[var(--text-tertiary)]" />
          Every figure links to the page it was read from, and the date it was read.
        </li>
        <li className="flex gap-2">
          <ChartIcon size={12} className="mt-0.5 shrink-0 text-[var(--text-tertiary)]" />
          A missing price is never shown as zero. Quote-only suppliers are listed
          and excluded from averages.
        </li>
      </ul>
      <Link
        to="/methodology"
        className="mt-3 inline-flex items-center gap-1 text-xs text-[var(--accent-text)] hover:underline"
      >
        How this index is built
        <ArrowRightIcon size={11} />
      </Link>
    </Panel>
  )
}

/* -- The rest of the product ----------------------------------------------- */
function MoreFrom() {
  return (
    <Panel>
      <RailHeading>More from NaijaIndex</RailHeading>
      <ul className="mt-3 space-y-1.5">
        {[
          ['Market data', '/markets'],
          ['Products & prices', '/products'],
          ['Businesses by state', '/states'],
          ['Market reports', '/reports'],
        ].map(([label, href]) => (
          <li key={href}>
            <Link
              to={href}
              className="text-xs text-[var(--text-secondary)] hover:text-[var(--accent-text)]"
            >
              {label}
            </Link>
          </li>
        ))}
      </ul>
      {/* Named, but not linked and not dressed up as a feature. A rail that
          advertises pages which do not exist teaches readers to distrust the
          cards above it that do. */}
      <p className="mt-3 border-t border-[var(--border-subtle)] pt-2.5 text-2xs leading-relaxed text-[var(--text-tertiary)]">
        In development: procurement, credit indicators and logistics. None of
        them are built yet, and nothing on this site depends on them.
      </p>
    </Panel>
  )
}

/* -- Shared ---------------------------------------------------------------- */
function RailHeading({ children, href }: { children: React.ReactNode; href?: string }) {
  if (href) {
    return (
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="field-label text-[var(--text-tertiary)]">{children}</h2>
        <Link
          to={href}
          className="text-2xs text-[var(--text-tertiary)] hover:text-[var(--accent-text)]"
        >
          View
        </Link>
      </div>
    )
  }
  return <h2 className="field-label text-[var(--text-tertiary)]">{children}</h2>
}
